import React from "react";
import Chart from "react-apexcharts";
import { useSelector } from "react-redux";

const chartStyle = {
  background: "#fff",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 0 10px rgba(0,0,0,0.05)",
  flex: 1,
};

const labelStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "6px",
  fontSize: "14px",
};

function PieChartBox({
  title,
  labels,
  series,
  colors,
  isSpecies = false,
  percentages,
  isBreed = false,
  avgApntList,
  avgAgeList,
}) {
  const options = {
    chart: {
      type: "donut",
    },
    labels: labels,
    legend: {
      show: false,
    },
    colors: colors,
    dataLabels: {
      enabled: true,
    },
    stroke: {
      width: 0,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const label = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];

        if (
          isSpecies &&
          percentages &&
          percentages[seriesIndex] !== undefined
        ) {
          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            label +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[seriesIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            value +
            " (" +
            percentages[seriesIndex] +
            ")</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        }

        if (isBreed && avgAgeList && avgApntList) {
          const avgAge = avgAgeList[seriesIndex] || 0;
          const avgAppts = avgApntList[seriesIndex] || 0;
          const total = series?.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            label +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[seriesIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            value +
            " (" +
            percentage +
            "%)</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Average Age: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            avgAge +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Average Appts per Pet: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            avgAppts +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        }

        return (
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          label +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          w.globals.colors[seriesIndex] +
          '; margin-left: 5px;"></span>' +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          value +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
  };

  return (
    <div style={chartStyle}>
      <h3>{title}</h3>
      <hr />
      <div style={{ display: "flex" }}>
        <div style={{ width: "50%" }}>
          <Chart options={options} series={series} type="donut" width="100%" />
        </div>
        <div
          style={{
            width: "50%",
            paddingLeft: "10px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {labels?.map((label, index) => {
            return (
              <div key={index} style={labelStyle}>
                <div
                  style={{ display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: colors[index],
                      display: "inline-block",
                    }}
                  ></span>
                  {label}
                </div>
                <div>{series[index]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function PieCharts() {
  const { dashboardData } = useSelector((state) => state.dashboard);

  if (!dashboardData || (!dashboardData.species && !dashboardData.breeds)) {
    return null; // or loading spinner
  }

  const species = dashboardData?.species || {};
  const breeds = dashboardData?.breeds || {};

  // Species chart
  const speciesLabels = ["Dog", "Cat", "Others"];
  const speciesValues = [
    species?.dogs?.count || 0,
    species?.cats?.count || 0,
    species?.others?.count || 0,
  ];
  const speciesPercentages = [
    species?.dogs?.percentage || 0,
    species?.cats?.percentage || 0,
    species?.others?.percentage || 0,
  ];
  const speciesColors = ["#004f9e", "#9ca9ff", "#dee6ff"];

  const breedEntries = Object.entries(breeds)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5); // Top 5 breeds
  const breedLabels = breedEntries?.map(([key]) => key);
  const breedSeries = breedEntries?.map(([, value]) => value?.count ?? 0);
  const breedColors = ["#004f9e", "#9ca9ff", "#dee6ff", "#c0d4ff", "#a5bfff"]; // 5 colors
  const avgApntList = breedEntries?.map(
    ([, value]) => value?.averageAppointmentsPerPet ?? 0
  );
  const avgAgeList = breedEntries?.map(([, value]) => value?.averageAge ?? 0);

  const speciesChart = {
    title: "Species",
    labels: speciesLabels,
    series: speciesValues,
    colors: speciesColors,
    isSpecies: true,
    percentages: speciesPercentages,
  };

  const breedsChart = {
    title: "Top Breeds",
    labels: breedLabels,
    series: breedSeries,
    colors: breedColors,
    avgApntList: avgApntList,
    avgAgeList: avgAgeList,
    isBreed: true,
  };

  return (
    <div style={{ display: "flex", gap: "20px", marginTop: "20px" }}>
      <PieChartBox {...speciesChart} />
      <PieChartBox {...breedsChart} />
    </div>
  );
}
